import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/LangToggle";
import { useI18n } from "@/context/I18nContext";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ScanLine, CreditCard, BellRing, FileSpreadsheet, ShieldCheck, Sparkles,
  Check, ArrowRight, TrendingUp,
} from "lucide-react";

function Stat({ value, label }) {
  return (
    <div>
      <div className="text-2xl md:text-3xl font-display font-bold text-cyan-300 tabular-nums whitespace-nowrap">{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const goLogin = () => navigate("/login");

  // Demo widget currency (display only, no conversion): EN shows USD, all other
  // languages follow the app's default PLN presentation.
  const cur = lang === "en" ? "USD" : "PLN";

  const FEATURES = [
    { icon: ScanLine, title: t("landing.feat1Title"), desc: t("landing.feat1Desc") },
    { icon: CreditCard, title: t("landing.feat2Title"), desc: t("landing.feat2Desc") },
    { icon: BellRing, title: t("landing.feat3Title"), desc: t("landing.feat3Desc") },
    { icon: TrendingUp, title: t("landing.feat4Title"), desc: t("landing.feat4Desc") },
    { icon: FileSpreadsheet, title: t("landing.feat5Title"), desc: t("landing.feat5Desc") },
    { icon: ShieldCheck, title: t("landing.feat6Title"), desc: t("landing.feat6Desc") },
  ];

  const FAQS = [
    { q: t("landing.faq1Q"), a: t("landing.faq1A") },
    { q: t("landing.faq2Q"), a: t("landing.faq2A") },
    { q: t("landing.faq3Q"), a: t("landing.faq3A") },
    { q: t("landing.faq4Q"), a: t("landing.faq4A") },
  ];

  const demoRows = [
    { m: t("landing.demoMerchant1"), a: 84.20, col: "#7CF7C2" },
    { m: t("landing.demoMerchant2"), a: 54.99, col: "#C792EA" },
    { m: t("landing.demoMerchant3"), a: 23.40, col: "#7AD7FF" },
  ];

  return (
    <div className="min-h-screen bg-surface-0 text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-surface-0/80 backdrop-blur">
        <div className="mx-auto max-w-[1200px] px-4 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2 md:gap-3">
            <a href="#pricing" className="hidden sm:block text-sm text-muted-foreground hover:text-foreground px-3">{t("landing.pricing")}</a>
            <a href="#features" className="hidden sm:block text-sm text-muted-foreground hover:text-foreground px-3">{t("landing.features")}</a>
            <LangToggle />
            <Button data-testid="nav-login-button" onClick={goLogin} className="h-9 bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">
              {t("landing.signIn")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden hero-glow">
        <div className="mx-auto max-w-[1200px] px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-400/5 px-3 py-1 text-xs text-cyan-200 mb-6">
              <Sparkles style={{ width: 14, height: 14 }} /> {t("landing.badge")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold tracking-tight leading-[1.05]">
              {t("landing.heroTitle1")}<br /><span className="text-cyan-300">{t("landing.heroTitle2")}</span> {t("landing.heroTitle3")}
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-[52ch]">
              {t("landing.heroSubtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button data-testid="landing-google-cta" onClick={goLogin} className="h-12 px-6 bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold text-base">
                {t("landing.continueGoogle")} <ArrowRight style={{ width: 18, height: 18 }} className="ml-2" />
              </Button>
              <a href="#pricing">
                <Button data-testid="landing-pricing-cta" variant="outline" className="h-12 px-6 w-full border-white/15 bg-white/5 hover:bg-white/10 text-base">
                  {t("landing.viewPricing")}
                </Button>
              </a>
            </div>
            <div className="mt-10 flex gap-8">
              <Stat value="10s" label={t("landing.statPerScan")} />
              <Stat value="10+" label={t("landing.statCategories")} />
              <Stat value="PL / EN / VI" label={t("landing.statLanguages")} />
            </div>
          </div>

          {/* Product preview */}
          <div className="relative">
            <div className="card-base p-5 elev-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("landing.thisMonth")}</p>
                  <p className="text-3xl font-display font-bold tabular-nums mt-1">{formatMoney(1284.50, cur)}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 bg-cyan-400/10 text-cyan-200 border border-cyan-400/25">
                  <TrendingUp style={{ width: 13, height: 13 }} /> +12%
                </span>
              </div>
              <div className="h-28 flex items-end gap-2">
                {[40, 65, 45, 80, 55, 95, 70].map((h, i) => (
                  <div key={i} className="flex-1 rounded-t-md bg-cyan-400/30" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="mt-4 space-y-2">
                {demoRows.map((r) => (
                  <div key={r.m} className="flex items-center justify-between rounded-lg bg-white/4 px-3 py-2">
                    <div className="flex items-center gap-2.5">
                      <span className="h-2 w-2 rounded-full" style={{ background: r.col }} />
                      <span className="text-sm">{r.m}</span>
                    </div>
                    <span className="font-mono text-sm tabular-nums">{formatMoney(r.a, cur)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-[1200px] px-4 md:px-8 py-16 md:py-20">
        <div className="max-w-[60ch]">
          <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">{t("landing.featuresTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("landing.featuresSubtitle")}</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="card-base p-5 hover:border-white/20 transition-colors duration-150">
              <div className="h-10 w-10 rounded-lg bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center mb-4">
                <f.icon className="text-cyan-300" style={{ width: 20, height: 20 }} />
              </div>
              <h3 className="font-display font-semibold">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-[1200px] px-4 md:px-8 py-16 md:py-20">
        <div className="text-center max-w-[55ch] mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">{t("landing.pricingTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("landing.pricingSubtitle")}</p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <div className="card-base p-7">
            <p className="text-sm uppercase tracking-wider text-muted-foreground">{t("landing.free")}</p>
            <p className="text-4xl font-display font-bold mt-2">{formatMoney(0, cur)}</p>
            <ul className="mt-6 space-y-3 text-sm">
              {[t("landing.freeF1"), t("landing.freeF2"), t("landing.freeF3"), t("landing.freeF4")].map((tx) => (
                <li key={tx} className="flex items-center gap-3"><Check className="text-cyan-300" style={{ width: 16, height: 16 }} />{tx}</li>
              ))}
            </ul>
            <Button onClick={goLogin} variant="outline" className="mt-7 w-full h-11 border-white/15 bg-white/5 hover:bg-white/10">{t("landing.getStartedFree")}</Button>
          </div>
          <div className="card-base p-7 border-cyan-400/30 relative glow-cyan">
            <span className="absolute top-5 right-5 text-xs rounded-full px-2.5 py-1 bg-cyan-400/15 text-cyan-200 border border-cyan-400/30">{t("landing.bestValue")}</span>
            <p className="text-sm uppercase tracking-wider text-cyan-200">{t("landing.premium")}</p>
            <p className="text-4xl font-display font-bold mt-2 text-cyan-300">{t("landing.premium")}</p>
            <ul className="mt-6 space-y-3 text-sm">
              {[t("landing.premF1"), t("landing.premF2"), t("landing.premF3"), t("landing.premF4"), t("landing.premF5")].map((tx) => (
                <li key={tx} className="flex items-center gap-3"><Check className="text-cyan-300" style={{ width: 16, height: 16 }} />{tx}</li>
              ))}
            </ul>
            <Button onClick={goLogin} className="mt-7 w-full h-11 bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">{t("landing.upgradePremium")}</Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[800px] px-4 md:px-8 py-16 md:py-20">
        <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-center mb-10">{t("landing.faqTitle")}</h2>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
              <AccordionTrigger className="text-left hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-[1200px] px-4 md:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <Logo size="sm" />
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <a href="/privacy" className="hover:text-foreground" data-testid="footer-privacy">{t("settings.privacy")}</a>
            <a href="/terms" className="hover:text-foreground" data-testid="footer-terms">{t("settings.terms")}</a>
            <a href="/ai-notice" className="hover:text-foreground" data-testid="footer-ai-notice">{t("settings.aiNotice")}</a>
          </div>
          <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} SubZero</p>
        </div>
      </footer>
    </div>
  );
}
